import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { ACTIVE_PAGE_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import {
  assetIdSchema,
  type WorkOrderId,
  workOrderIdSchema,
} from "../lib/workIds";

const lookup = z.object({ code: z.string(), name: z.string() });
const person = z.object({
  id: z.number(),
  name: z.string(),
  initials: z.string(),
});
/**
 * Server-computed per-action availability (IWA-19230) so Web never infers
 * permissions from stale local role state — only from the latest API
 * response.
 */
const workOrderActions = z.object({
  REOPEN: z.boolean(),
  // Defaults to false so an older API response hides the attach control.
  ATTACH_PROCEDURE: z.boolean().default(false),
  EDIT: z.boolean(),
  ASSIGN: z.boolean(),
  TRANSITION: z.boolean(),
  COMPLETE: z.boolean(),
  CANCEL: z.boolean(),
  DELETE: z.boolean(),
  RESTORE: z.boolean(),
  // Defaults to false so an older API response hides the self-assign control.
  CLAIM: z.boolean().default(false),
});
const responseSchema = z.object({
  workOrder: z.object({
    id: workOrderIdSchema,
    version: z.string().regex(/^[1-9]\d{0,9}$/),
    number: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    instructions: z.string().nullable(),
    cancellationReason: z.string().nullable(),
    // Optional so an older API response simply leaves the field empty.
    completionNote: z.string().nullable().optional(),
    status: lookup,
    priority: lookup.nullable(),
    workType: lookup,
    plannerGroup: lookup.nullable(),
    assignee: person.nullable(),
    asset: z
      .object({
        id: assetIdSchema,
        code: z.string(),
        name: z.string(),
        location: z.string().nullable(),
        category: z.string().nullable(),
      })
      .nullable(),
    // Shared Categories on the Work Order; `active` is false for one retired
    // after it was added. Defaults to none so an older API response still
    // parses.
    categories: z
      .array(
        z.object({
          id: z.number().int().positive(),
          name: z.string(),
          active: z.boolean(),
        }),
      )
      .default([]),
    // Active Categories the facility may add.
    categoryOptions: z
      .array(z.object({ id: z.number().int().positive(), name: z.string() }))
      .default([]),
    schedule: z.object({
      basicStartDate: z.string().datetime().nullable(),
      basicFinishDate: z.string().datetime().nullable(),
      dueDate: z.string().datetime().nullable(),
    }),
    lifecycle: z.object({
      createdBy: z.string().nullable(),
      createdOn: z.string().datetime().nullable(),
      updatedOn: z.string().datetime().nullable(),
      completedBy: z.string().nullable(),
      completedOn: z.string().datetime().nullable(),
    }),
    costs: z.object({
      labor: z.number().nullable(),
      contract: z.number().nullable(),
      material: z.number().nullable(),
      total: z.number(),
      currency: z.string().nullable(),
    }),
    metadata: z.object({
      createdBySource: z.string().nullable(),
      requestedBySource: z.string().nullable(),
      revision: z.string().nullable(),
      orderSystemStatus: z.string().nullable(),
      orderUserStatus: z.string().nullable(),
      statusDescriptionLine: z.string().nullable(),
      priorityText: z.string().nullable(),
      catalogProfile: z.string().nullable(),
      equipmentCode: z.string().nullable(),
      latestSourceName: z.string().nullable(),
      latestSourceDate: z.string().datetime().nullable(),
      sourceList: z.string().nullable(),
      sourceUpdatedAt: z.string().datetime().nullable(),
      lastSyncedAt: z.string().datetime().nullable(),
    }),
    history: z.array(
      z.object({
        eventType: lookup,
        occurredOn: z.string().datetime(),
        actor: z.string(),
        provenance: z.object({
          kind: z.enum(["imported", "in-product"]),
          label: z.string(),
        }),
        related: z.string().nullable(),
        changes: z.array(
          z.object({ field: z.string(), from: z.string(), to: z.string() }),
        ),
      }),
    ),
    canComplete: z.boolean(),
    actions: workOrderActions,
    // Status-menu targets the server allows for this caller; `COMPLETED`
    // opens the Complete action. Defaults to none so an older API response
    // renders a read-only status instead of failing to parse.
    statusTargets: z.array(z.string()).default([]),
  }),
});

export type WorkOrderDetail = z.infer<typeof responseSchema>["workOrder"];

export async function fetchWorkOrderDetail(
  customerId: string,
  facilityId: string,
  workOrderId: WorkOrderId,
) {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["work-orders"][":workOrderId"].$get({
    param: { customerId, facilityId, workOrderId },
  });
  if (!response.ok) {
    throw new Error(`Failed to load Work Order: ${response.status}`);
  }
  return responseSchema.parse(await response.json());
}

export function useWorkOrderDetailQuery(workOrderId?: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return useQuery(
    workOrderDetailQueryOptions({
      customerId: selectedCustomer?.id,
      facilityId: selectedFacility?.id,
      workOrderId,
    }),
  );
}

export function workOrderDetailQueryOptions({
  customerId,
  facilityId,
  workOrderId,
}: {
  customerId?: string;
  facilityId?: string;
  workOrderId?: WorkOrderId;
}) {
  return queryOptions({
    queryKey: ["work-order-detail", customerId, facilityId, workOrderId],
    queryFn: () => {
      if (!customerId || !facilityId || !workOrderId) {
        throw new Error(
          "A customer, facility, and Work Order must be selected.",
        );
      }
      return fetchWorkOrderDetail(customerId, facilityId, workOrderId);
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    meta: { errorMessage: "Failed to load Work Order." },
  });
}
